import React, { useEffect, useState } from 'react';

// Import necessary components from react-bootstrap
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import { useError } from "../../contexts/ErrorContext";
import { VideosAPI } from '../../services/api';
import { Video } from '../../types';

interface VideoModalProps {
  show: boolean;
  onHide: () => void;
  video: Video | null;
  is_refreshed?: boolean;
}

const VideoModal: React.FC<VideoModalProps> = ({ show, onHide, video, is_refreshed }) => {
  // Use a more descriptive name and proper null typing for state
  const [currentVideo, setCurrentVideo] = useState<Video | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setError } = useError();

  useEffect(() => {
    // A cleanup flag to prevent state updates on an unmounted component
    let isMounted = true;

    // Guard clause: do nothing if there's no video object
    if (!video) {
      return;
    }

    // Reset state when the video prop changes
    setCurrentVideo(null);
    setError(null);

    if (is_refreshed) {
      // If the video URL is already fresh, just use it directly
      setCurrentVideo(video);
    } else {
      // Otherwise, fetch the refreshed URL
      setIsLoading(true);
      VideosAPI.refreshUrl(video.id)
        .then((response) => {
          if (isMounted) {
            setCurrentVideo(response.data);
          }
        })
        .catch(() => {
          if (isMounted) {
            setError('Failed to load the video. Please try again.');
          }
        })
        .finally(() => {
          if (isMounted) {
            setIsLoading(false);
          }
        });
    }

    // Cleanup function: runs when the component unmounts or dependencies change
    return () => {
      isMounted = false;
    };
  }, [video, is_refreshed]); // Effect dependencies

  // Helper function for rendering the modal body content
  const renderModalBody = () => {
    if (isLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      );
    }

    if (currentVideo?.url) {
      return (
        <video controls autoPlay className="w-100">
          <source src={currentVideo.url} type={currentVideo.content_type} />
          Your browser does not support the video tag.
        </video>
      );
    }

    return null; // Should not happen in normal flow, but a good fallback
  };

  // Don't render the modal at all if it's not shown or has no initial video data
  if (!show || !video) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {video.title}
          {video.is_master && <Badge bg="primary" className="ms-2">Master</Badge>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {renderModalBody()}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          as="a"
          // Disable the button if there's no video URL (e.g., during load or error)
          disabled={!currentVideo?.url}
          href={currentVideo?.url}
          target="_blank"
          download
        >
          Download
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VideoModal;